import { ComputedRef, InjectionKey } from 'vue';

import {
	ProjectId,
	ServiceContextMap,
	ServiceContextMapId,
	ServiceContextMapKey,
	ServiceSlug,
	WorkflowId,
} from '@ply-io/ply-front-shared-utilities';
import { RenderableWidgetEventData } from '@ply-io/runner';

export type MappedFields = Record<string, unknown>;
type RequireAtLeastOne<T, Keys extends keyof T = keyof T> = Pick<T, Exclude<keyof T, Keys>> &
	{
		[K in Keys]-?: Required<Pick<T, K>> & Partial<Pick<T, Exclude<Keys, K>>>;
	}[Keys];
export type ServiceContextMapIdKey = { id: ServiceContextMapId; key: ServiceContextMapKey };
export type GetMappedFields = (payload: RequireAtLeastOne<ServiceContextMapIdKey, 'id' | 'key'>) => Promise<MappedFields> | MappedFields;
export type EditingWorkflowState = { id: WorkflowId; onCreation: boolean };
export interface TileCreationInterface {
	contextToCreateTile: ServiceContextMap | undefined | null;
}
export interface WorkflowsInjectionInterface {
	runWorkflow: (workflowId: WorkflowId) => void;
	startCreateTile: () => void;
	createWorkflow: (payload: ContextIdServiceSlugEntry) => Promise<void>;
	getMappedFields: (payload: ServiceContextMapIdKey) => ReturnType<GetMappedFields>;
	styleEditingWorkflowId: ComputedRef<EditingWorkflowState | null>;
	clearStyleEditingWorkflowId: () => void;
}
export interface ExpansionInterface {
	expandTile: (workflowId: WorkflowId) => void;
	clearExpansion: () => void;
	expandedTile: ComputedRef<WorkflowId | null>;
	expandedContext: ComputedRef<ServiceContextMapId | null>;
}
export const TileCreationKey: InjectionKey<ComputedRef<TileCreationInterface>> = Symbol('TileCreationKey');
export const WorkflowsKey: InjectionKey<WorkflowsInjectionInterface> = Symbol('WorkflowsKey');

export const ExpansionKey: InjectionKey<ExpansionInterface> = Symbol('ExpansionKey');

export interface TileContextMenuFunctionsInterface {
	editButtonStyle: (workflowId: WorkflowId) => void;
	editWorkflow: (workflowId: WorkflowId, projectId: ProjectId) => void;
	deleteWorkflow: (workflowId: WorkflowId) => void;
	openShareWorkflow: (workflowId: WorkflowId) => void;
	openEditSetup: (workflowId: WorkflowId) => void;
	setIsVisible: (workflowId: WorkflowId, isVisible: boolean) => void;
}
export const TileContextMenuFunctionsKey: InjectionKey<TileContextMenuFunctionsInterface> = Symbol('TileContextMenuFunctionsKey');

export interface WidgetContentInterface {
	showWidget: ComputedRef<boolean>;
	widgetToRender: ComputedRef<Record<string, unknown> | null>;
	handleWidgetNode: (payload: RenderableWidgetEventData) => void;
}
export const WidgetContentKey: InjectionKey<WidgetContentInterface> = Symbol('WidgetContentKey');

export interface SideBarLoginInterface {
	loginClicked: () => void;
}
export const SideBarLoginKey: InjectionKey<SideBarLoginInterface> = Symbol('SideBarLoginKey');

export interface NodeRunErrorDialogState {
	actionTitle: string;
	msg: string;
	serviceSlug: string;
	editStepUrl: string;
	dismiss: () => void;
}
export interface NodeRunErrorDialogInterface {
	clearNodeRunErrorDialog: (clearFlowRun: boolean) => void;
	setNodeRunErrorDialogState: (payload: NodeRunErrorDialogState) => void;
	nodeRunErrorDialogState: ComputedRef<NodeRunErrorDialogState | null>;
}

export const NodeRunErrorDialogKey: InjectionKey<NodeRunErrorDialogInterface> = Symbol('NodeRunErrorDialogKey');

interface SideBarContextMenuEmits {
	(e: 'edit-button-style', workflowId: WorkflowId): void;
	(e: 'edit-workflow', workflowId: WorkflowId): void;
	(e: 'delete-workflow', workflowId: WorkflowId): void;
	(e: 'open-share-workflow', workflowId: WorkflowId): void;
	(e: 'open-edit-setup', workflowId: WorkflowId): void;
}

export type ContextIdServiceSlugEntry = { contextId: ServiceContextMapId; slug: ServiceSlug };

export interface SideBarEmits extends SideBarContextMenuEmits {
	(e: 'start-create-tile'): void;
	(e: 'run-workflow', workflowId: WorkflowId): void;
	(e: 'close'): void;
	(e: 'toggle-sidebar-alignment'): void;
	(e: 'toggle-pin'): void;
	(e: 'open-settings'): void;
	(e: 'workflow-clicked', workflowId: WorkflowId): void;
}

export type ContextKeyToCreateTile = ContextIdServiceSlugEntry | null;

export interface Chip {
	text: string;
	value: unknown;
	icon?: string;
}

export interface ContextChipManager {
	selectedChip: Chip | null;
	setSelectedChip: (chip: Chip | null) => void;
	chips: Chip[];
	param: string;
}
